import { API_BASE_URL } from './constants';

/** Lỗi có ngữ cảnh để UI phân biệt được "mạng chết" và "API trả 500". */
export class ApiError extends Error {
	readonly status: number;
	readonly url: string;

	constructor(message: string, status: number, url: string) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.url = url;
	}
}

const REQUEST_TIMEOUT_MS = 15_000;

export type QueryParams = Record<
	string,
	string | number | boolean | null | undefined
>;

/**
 * Dựng URL tuyệt đối tới API, tự bỏ các param rỗng/undefined để không gửi
 * `?category=` vô nghĩa lên server.
 */
export function buildApiUrl(path: string, params: QueryParams = {}): string {
	const url = new URL(
		path.startsWith('/') ? `${API_BASE_URL}${path}` : `${API_BASE_URL}/${path}`,
	);

	for (const [key, value] of Object.entries(params)) {
		if (value === null || value === undefined || value === '') continue;
		url.searchParams.set(key, String(value));
	}

	return url.toString();
}

/**
 * fetch + JSON + timeout, có tôn trọng signal của TanStack Query nên khi người
 * dùng gõ tiếp vào ô tìm kiếm, request cũ bị huỷ thật sự (bản gốc dùng biến
 * `requestId` để bỏ qua kết quả cũ — request vẫn chạy tới cùng).
 */
export async function fetchJson<T>(
	url: string,
	signal?: AbortSignal,
): Promise<T> {
	const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
	const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;

	let response: Response;
	try {
		response = await fetch(url, {
			signal: combined,
			headers: { Accept: 'application/json' },
		});
	} catch (error) {
		if (signal?.aborted) throw error;
		if (error instanceof DOMException && error.name === 'TimeoutError') {
			throw new ApiError('Máy chủ phản hồi quá lâu, thử lại nhé.', 408, url);
		}
		throw new ApiError('Không kết nối được tới máy chủ phim.', 0, url);
	}

	if (!response.ok) {
		throw new ApiError(
			`Máy chủ phim trả về lỗi ${response.status}.`,
			response.status,
			url,
		);
	}

	try {
		return (await response.json()) as T;
	} catch {
		throw new ApiError('Dữ liệu trả về không đúng định dạng.', 502, url);
	}
}
